/**
 * Session-scoped legend visibility logic for the dossier MOVES tab
 * (DR-10 / SR-10 / EN-13, 2026-05-13).
 *
 * The MOVES tab uses a compressed efficacy chip set per DR-7 (4× / 2× /
 * ¼× / 0× — ½× chips omitted). That introduces a semantic ambiguity:
 * "no chip" could mean "neutral 1×" OR "resisted ½×". To resolve, the
 * MOVES tab shows a one-line legend on FIRST open per session:
 *
 *   ● phy · ○ spc · chips show super-effective + ineffective only
 *
 * Tap-dismissal OR the first tab-switch-away clears it. Reopening MOVES
 * in the same session shows no legend; a new session (page reload,
 * fresh tab) brings it back. Persistence is `sessionStorage`-scoped —
 * NOT localStorage — so the legend reliably reappears in long-term use.
 *
 * EN-13: vitest is Node-only with no `sessionStorage` global. The three
 * functions below take an injected `Storage`-shaped argument so tests
 * can inject a Map-backed mock; the Phaser shell at `dossierTabStrip.ts`
 * (or wherever the MOVES tab body renders) passes the real
 * `sessionStorage`. Mirrors the careerStore.load() localStorage
 * abstraction pattern used elsewhere in the codebase.
 */

/** localStorage / sessionStorage key. Namespaced to avoid collision with
 *  the careerStore's `poke:career` key + future `poke:*` entries. */
export const LEGEND_STORAGE_KEY = 'poke:dossier-legend';

/** Sentinel value written when the legend has been seen. The exact
 *  string is opaque to callers — `isLegendSeen` reads it through the
 *  same helper, so the value can change without consumer churn. */
const SEEN_SENTINEL = '1';

/**
 * Returns whether the legend SHOULD be shown given the seen-flag state.
 * A trivial wrapper (`!seen`) — exposed so the call site reads as a
 * single intent ("if shouldShowLegend(...) → render banner") rather
 * than negating a boolean in line.
 */
export function shouldShowLegend(seen: boolean): boolean {
  return !seen;
}

/**
 * Mark the legend as seen in the provided storage. Silent on failure
 * (private-browsing sandbox / quota exceeded). Matches the careerStore
 * try/catch posture — legend re-appearance is a minor degradation, not
 * a gameplay-breaking bug.
 */
export function markLegendSeen(storage: Storage): void {
  try {
    storage.setItem(LEGEND_STORAGE_KEY, SEEN_SENTINEL);
  } catch {
    // ignore — legend will re-appear; acceptable degradation
  }
}

/**
 * Read the seen-flag from the provided storage. Returns `false` when:
 *   - Key is absent (first session).
 *   - Read throws (private-browsing sandbox).
 *   - Stored value is anything other than `SEEN_SENTINEL` (corrupt /
 *     legacy / future-schema entry — treat as unseen, will be overwritten
 *     when the user dismisses).
 */
export function isLegendSeen(storage: Storage): boolean {
  try {
    return storage.getItem(LEGEND_STORAGE_KEY) === SEEN_SENTINEL;
  } catch {
    return false;
  }
}
